import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseRounds } from "../../helpers/karkhanaEnabled";
import { createPaySheet, getNotCounted, getPaySheetPreview, listPaySheets } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import { defaultRange, isDraftSheet, lineSummary, quickRanges } from "./pay/paySheetModel";
import { rangeLabel } from "./daily/dailyModel";
import { hours } from "./punches/punchModel";
import "./daily/karkhana-daily.scss";
import "./pay/karkhana-pay.scss";
import { fmtDay, rupees, todayIso } from "./rates/rateModel";
import "./workers/karkhana-workers.scss";
import "./rounds/karkhana-rounds.scss";
import { LangToggle, useKT } from "./i18n/kt";
import PageHeader from "./ui/PageHeader";
import CorrectionLines from "./pay/CorrectionLines";
import { kp } from "./karkhanaPaths";
import { useCashbookStatus } from "../../helpers/cashbookStatus";
import { listLivePrints } from "../../services/cashBookApi";
import type { PrintView } from "./cashbook/types";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

/** One earlier sheet's paper: not printed yet, photo still to send, sent, or checked. */
const SheetPrintState = ({ sheetId, print, unpaid }: { sheetId: string; print?: PrintView; unpaid: boolean }) => {
  const { t } = useKT();
  if (!print)
    return !unpaid ? null : (
      <Link to={kp.paySheet(sheetId, { print: "1" })} className="kw-chip">
        {t("Print")}
      </Link>
    );
  if (print.checked) return <span className="kw-chip is-ok">{t("Checked ✓")} · {print.sheetNo}</span>;
  if (print.pagesSent.length >= print.pages) return <span className="kw-chip is-ok">{t("Sent ✓")} · {print.sheetNo}</span>;
  return (
    <Link to={kp.cashCamera({ kind: "PAY_SHEET", printId: print.printId })} className="kw-chip is-review">
      {t("Send photo")} · {print.sheetNo}
    </Link>
  );
};

/**
 * Pick the days to pay and see who gets what before making the sheet (Figma 5c): everyone's net,
 * a warning for work that has no rate yet, then Make pay sheet or Print. Earlier sheets below.
 */
const KarkhanaPaySheetsPage = () => {
  const { t, lang } = useKT();
  const history = useHistory();
  const role = readRole();
  const [range, setRange] = useState(defaultRange);
  const [sheets, setSheets] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [unrated, setUnrated] = useState({ entries: 0, people: 0 });
  const [picking, setPicking] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  // Cash book add-on: each earlier sheet says Print, Send photo, Sent ✓ or Checked ✓ (V1_SPEC 11.4 E3).
  const cashbook = useCashbookStatus();
  const cashOn = cashbook.loaded && cashbook.enabled;
  const [prints, setPrints] = useState<Record<string, PrintView>>({});
  useEffect(() => {
    if (!cashOn) return;
    let live = true;
    listLivePrints(14)
      .then((res) => {
        if (!live || !res?.success) return;
        const by: Record<string, PrintView> = {};
        (res.data?.prints || []).forEach((p) => {
          if (p.state === "LIVE") by[p.paySheetId] = p;
        });
        setPrints(by);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [cashOn]);

  useEffect(() => {
    if (!canUseRounds(role)) {
      history.replace(kp.home());
      return;
    }
    listPaySheets()
      .then((res: any) => setSheets(res?.success ? res.data || [] : []))
      .catch(() => setSheets([]));
    // Work without a rate is left off the sheet until the rate is filled: say so before making it.
    getNotCounted()
      .then((res: any) => {
        const d = res?.success ? res.data : null;
        const entries = (d?.loom || []).reduce((n: number, g: any) => n + (Number(g.entries) || 0), 0);
        setUnrated({ entries, people: d?.time?.length || 0 });
      })
      .catch(() => setUnrated({ entries: 0, people: 0 }));
  }, [role, history]);

  // Who the sheet would pay for these days, worked out without making it.
  useEffect(() => {
    let live = true;
    setPreview(null);
    getPaySheetPreview(range.from, range.to)
      .then((res: any) => live && setPreview(res?.success ? res.data : { lines: [] }))
      .catch(() => live && setPreview({ lines: [] }));
    return () => {
      live = false;
    };
  }, [range.from, range.to]);

  const make = async (print = false) => {
    setBusy(true);
    try {
      const res: any = await createPaySheet(range.from, range.to);
      if (!res?.success) {
        notifications({ title: t("Error"), message: t(res?.message || "Could not make the sheet"), type: "danger" });
        return;
      }
      if (Array.isArray(res.data?.lines) && !res.data.lines.length) {
        notifications({ title: t("Nobody to pay on these dates"), message: t("No work or attendance between {{from}} and {{to}}. Check the dates.", { from: fmtDay(range.from), to: fmtDay(range.to) }), type: "warning" });
      }
      history.push(kp.paySheet(res.data.id, { print: print ? "1" : null }));
    } catch {
      notifications({ title: t("No connection"), message: t("Making a pay sheet needs a connection."), type: "warning" });
    } finally {
      setBusy(false);
    }
  };

  if (!canUseRounds(role)) return null;

  const picks = quickRanges();
  const lines: any[] = preview?.lines || [];
  const summary = (l: any) => {
    const x = lineSummary(l);
    return [
      x.inches ? t("{{n}} in", { n: x.inches }) : "",
      x.minutes ? hours(x.minutes) : "",
      x.days ? t(x.days === 1 ? "1 day" : "{{n}} days", { n: x.days }) : "",
      x.cut ? t("advance cut {{amount}}", { amount: rupees(x.cut, 0) }) : "",
    ]
      .filter(Boolean)
      .join(" · ");
  };

  return (
    <div className="gi-registry-page kw-page kr-rounds kpy-page has-foot">
      <PageHeader
        title={
          <>
            {t("Pay sheet")}
            <small>{rangeLabel(range.from, range.to, lang)}</small>
          </>
        }
        back={kp.pay()}
        backLabel={t("Back")}
      >
        <LangToggle />
      </PageHeader>

      <section className="kps-days">
        <span className="kps-label">{t("Days to pay")}</span>
        <div className="kd-chips" role="group" aria-label={t("Days to pay")}>
          {picks.map((q) => {
            const on = !picking && range.from === q.from && range.to === q.to;
            return (
              <button key={q.key} type="button" className={`kr-choice${on ? " is-on" : ""}`} aria-pressed={on} onClick={() => { setPicking(false); setRange({ from: q.from, to: q.to }); }}>
                {t(q.label)}
                {on ? ` · ${rangeLabel(q.from, q.to, lang)}` : ""}
              </button>
            );
          })}
          <button type="button" className={`kr-choice${picking ? " is-on" : ""}`} aria-pressed={picking} onClick={() => setPicking(true)}>
            {t("Pick…")}
          </button>
        </div>
        {picking ? (
          <div className="kps-pick">
            <input type="date" value={range.from} max={range.to} onChange={(e) => e.target.value && setRange({ ...range, from: e.target.value })} aria-label={t("First day")} />
            <span>{t("to")}</span>
            <input type="date" value={range.to} min={range.from} max={todayIso()} onChange={(e) => e.target.value && setRange({ ...range, to: e.target.value })} aria-label={t("Last day")} />
          </div>
        ) : null}
      </section>

      <div className="kpay-balances">
        <div className="kpay-balance">
          <strong>{preview ? lines.length : "…"}</strong>
          <span>{t("people")}</span>
        </div>
        <div className="kpay-balance">
          <strong>{preview ? rupees(preview.totals?.netDue || 0, 0) : "…"}</strong>
          <span>{t("net to pay")}</span>
        </div>
      </div>

      {!preview ? <div className="kw-empty">{t("Loading…")}</div> : null}
      {preview && !lines.length ? <div className="kw-empty">{t("Nobody to pay for these days yet.")}</div> : null}
      {lines.length ? (
        <ul className="kpy-people">
          {lines.map((l) => (
            <li key={l.personId}>
              <div className="kpy-person">
                <span className="kpy-person__who">
                  <strong>
                    <span className="kw-code">{l.code}</span> {l.name}
                  </strong>
                  <span>{summary(l)}</span>
                </span>
                <span className="kpy-person__amt">
                  {l.issues ? <span className="kd-chip is-warn">{t("rate")}</span> : null}
                  <strong>{rupees(l.netDue, 0)}</strong>
                </span>
              </div>
              <CorrectionLines line={l} />
            </li>
          ))}
        </ul>
      ) : null}

      {unrated.entries || unrated.people ? (
        <Link to={kp.pay(undefined, "not-counted")} className="kr-sheet-warn">
          {unrated.entries
            ? t(unrated.entries === 1 ? "1 entry still has no rate. Fill it first, or it stays for next week." : "{{n}} entries still have no rate. Fill them first, or they stay for next week.", { n: unrated.entries })
            : null}
          {unrated.entries && unrated.people ? " " : null}
          {unrated.people
            ? t(unrated.people === 1 ? "1 person has no day rate, so their hours are not paid yet." : "{{n}} people have no day rate, so their hours are not paid yet.", { n: unrated.people })
            : null}
        </Link>
      ) : null}

      {sheets && sheets.length ? (
        <section className="kps-earlier">
          <h3 className="km-heading">{t("Earlier sheets")}</h3>
          <ul className="kpy-people">
            {sheets.map((x) => (
              <li key={x.id}>
                <Link to={kp.paySheet(x.id)} className="kpy-person">
                  <span className="kpy-person__who">
                    <strong>
                      {rangeLabel(x.from, x.to, lang)}
                      {isDraftSheet(x) ? <span className="kw-chip is-review" style={{ marginLeft: 8 }}>{t("Draft")}</span> : null}
                    </strong>
                    <span>
                      {t("{{paid}} of {{n}} paid", { paid: x.paid, n: x.people })} · {x.createdByName}
                    </span>
                  </span>
                  <span className="kpy-person__amt">
                    <strong>{rupees(x.netDue, 0)}</strong>
                  </span>
                </Link>
                {cashOn && !isDraftSheet(x) ? <SheetPrintState sheetId={x.id} print={prints[x.id]} unpaid={Number(x.paid) < Number(x.people)} /> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="kf-foot">
        <button type="button" className="kd-btn kd-btn--line" disabled={busy || !lines.length} onClick={() => make(true)}>
          {t("Print / PDF")}
        </button>
        <button type="button" className="kd-btn" disabled={busy || !lines.length} onClick={() => make(false)}>
          {busy ? t("Making…") : t("Make pay sheet")}
        </button>
      </div>
    </div>
  );
};

export default KarkhanaPaySheetsPage;
